import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
    computed,
    inject,
    signal,
} from '@angular/core';

import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { EventModel, SetTab } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportsThemedNotificationDirective, ThemeNotificationVariant } from '@frontend/sports/common/ui/sports-themed-notifcation-bubble';
import { LinkDirective } from '@frontend/sports/common/ui/sports-themed-pills';
import { TooltipModule } from '@frontend/sports/tooltip-feature';
import { DsArrow } from '@frontend/ui/arrow';
import { DsBadge } from '@frontend/ui/badge';
import { DsDivider } from '@frontend/ui/divider';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsTabsModule } from '@frontend/ui/tabsgroup';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { NgxPopperjsContentComponent, NgxPopperjsModule, NgxPopperjsPlacements } from 'ngx-popperjs';

import { PriceboostCountSignpostComponent } from '../../../shared-priceboost/components/priceboost-count-signpost.component';
import { EventMarketsTabBarItem } from './event-header-tab-bar.models';
import { EventHeaderTabsTooltipService } from './event-header-tabs-tooltip.service';

@Component({
    selector: 'ms-event-header-tabs',
    templateUrl: './event-header-tabs.component.html',
    styleUrls: ['./event-header-tabs.component.scss'],
    styles: [
        `
            :host {
                display: block;
            }
        `,
    ],
    imports: [
        DsTabsModule,
        PriceboostCountSignpostComponent,
        DsBadge,
        TooltipModule,
        NgxPopperjsModule,
        SpeculativeLinkDirective,
        LinkDirective,
        DsDivider,
        DsNotificationBubble,
        SportsThemedNotificationDirective,
        DsArrow,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventHeaderTabsComponent implements OnChanges {
    @Input() tabs: EventMarketsTabBarItem[];
    @Input() event: EventModel;
    @Output() activeTab = new EventEmitter<EventMarketsTabBarItem>();
    @ViewChild('tooltipPopper') tooltipPopper: NgxPopperjsContentComponent;

    variant: ThemeNotificationVariant = {
        'default': 'neutral',
        'mgm-4': 'utility',
        'sports-interaction': 'utility',
    };

    selectedTabBubbleVariant: ThemeNotificationVariant = {
        'default': 'primary',
        'mgm-4': 'utility',
        'sports-interaction': 'utility',
    };

    listItems = signal<EventMarketsTabBarItem[]>([]);
    viewListItems = computed(() => {
        const tooltip = this.tooltip();
        return this.listItems().map((tab) => {
            return {
                ...tab,
                hasTooltip: this.displayTooltip(tab, tooltip.shouldShow),
            };
        });
    });

    moreText: string;
    tooltip = signal({ shouldShow: false, message: '' });
    betBuilderTabId = SetTab.BetBuilder;
    activeName = computed(
        () =>
            this.listItems()
                .find((t) => !!t.active)
                ?.id?.toString() ??
            this.listItems()[0]?.id?.toString() ??
            '',
    );

    private ngZone = inject(NgZone);

    constructor(
        public sitecore: Sitecore,
        private tooltipService: EventHeaderTabsTooltipService,
        private strategy: RxStrategyProvider,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tabs']) this.updateItems();
        if (changes['event']) {
            this.tooltip.set({
                message: this.event.betBuilderInfo?.betBuilderId
                    ? this.sitecore.globalMessages.BetBuilderAngstromTooltip
                    : this.sitecore.globalMessages.BetBuilderComboTooltip,
                shouldShow: this.tooltipService.shouldShowTooltip(this.event.betBuilderInfo?.betBuilderProvider),
            });
        }
    }

    selected(name: string): void {
        const selectedTab = this.listItems().find((t) => t.id.toString() === name);
        if (selectedTab) {
            this.tabs.forEach((tab) => (tab.active = tab.id === selectedTab.id));
            this.listItems.update((tabs) => tabs.map((t) => ({ ...t, active: t.id === selectedTab.id })));
            this.strategy
                .schedule(() => this.activeTab.emit(selectedTab), {
                    scope: this,
                    patchZone: this.ngZone,
                })
                .subscribe();
        }
    }

    onPopperShown() {
        this.tooltipService.increaseCount();
        this.tooltipPopper.onDocumentResize();
    }

    closeTooltip() {
        this.tooltipService.closeTooltip();
        this.tooltip.update((t) => ({ ...t, shouldShow: false }));
    }

    private displayTooltip(item: EventMarketsTabBarItem, shouldShow: boolean): boolean {
        return (
            (item.id === this.betBuilderTabId || !!item.isSgp || this.event.betBuilderInfo?.betBuilderProvider === BetBuilderProvider.BybFootball) &&
            !!item.active &&
            shouldShow
        );
    }

    private updateItems(): void {
        this.listItems.set(this.tabs ?? []);
    }

    protected readonly NgxPopperjsPlacements = NgxPopperjsPlacements;
}
